import type {ReactNode, ComponentType} from "react";
import {useState, useEffect, createContext, useMemo, useContext} from "react";
import {MiniEvent} from "@cdx/common";
import {Box, Col, css, DSButton, DSIcon, DSIconButton, Row, Text} from "@cdx/ds";
import {useModalWithData} from "../components/Modal/Modals";

type ConfirmInfo = {
  title: string | ReactNode;
  content?: string | ReactNode;
  comp?: ComponentType<any>;
  compProps?: any;
  confirmLabel?: string;
  cancelLabel?: string | false | null;
  resolve: (ok: boolean) => void;
  width?: number;
};

const ConfirmContext = createContext<{handleNo: () => void; handleYes: () => void} | null>(null);

const confirmEvent = new MiniEvent<ConfirmInfo>();

export const useConfirmContext = () => useContext(ConfirmContext)!;

export const ConfirmationRegistry = () => {
  const [current, setCurrent] = useState<ConfirmInfo | null>(null);
  useEffect(() => {
    return confirmEvent.addListener((next) => {
      setCurrent((prev) => {
        if (prev) prev.resolve(false);
        return next;
      });
    });
  }, []);

  const context = useMemo(
    () => ({
      handleNo: () => {
        if (current) {
          current.resolve(false);
          setCurrent(null);
        }
      },
      handleYes: () => {
        if (current) {
          current.resolve(true);
          setCurrent(null);
        }
      },
    }),
    [current]
  );

  const renderModal = useModalWithData(current, {
    onClose: context.handleNo,
    colorTheme: "white",
    width: current?.width ?? 480,
    hideClose: true,
  });
  return renderModal(
    ({title, content, cancelLabel, confirmLabel, comp: Comp, compProps}: ConfirmInfo) => (
      <ConfirmContext.Provider value={context}>
        <Col
          divideX
          data-cdx-context={typeof title === "string" ? `Confirmation-${title}` : "Confirmation"}
        >
          <Row py="12px" px="24px" align="center">
            {typeof title === "string" ? (
              <Text type="label16" as="h2" color="primary">
                {title}
              </Text>
            ) : (
              title
            )}
            <Box ml="auto">
              <DSIconButton
                icon={<DSIcon name="Close" label="Close" />}
                onClick={context.handleNo}
                variant="tertiary"
                negatePadding
                size="md"
              />
            </Box>
          </Row>
          {Comp ? (
            <Box py="16px" px="24px">
              <Comp {...compProps} onCancel={context.handleNo} onConfirm={context.handleYes} />
            </Box>
          ) : (
            <Col py="16px" px="24px" sp="32px">
              {typeof content === "string" ? (
                <Text type="prose16" color="secondary">
                  {content}
                </Text>
              ) : (
                content
              )}
              <Row sp="32px">
                {cancelLabel && (
                  <DSButton size="lg" variant="secondary" onClick={context.handleNo}>
                    {cancelLabel}
                  </DSButton>
                )}
                <DSButton
                  size="lg"
                  variant="primary"
                  onClick={context.handleYes}
                  autoFocus
                  className={css({ml: "auto"})}
                >
                  {confirmLabel}
                </DSButton>
              </Row>
            </Col>
          )}
        </Col>
      </ConfirmContext.Provider>
    )
  );
};

const confirm = ({
  title,
  content,
  comp,
  compProps,
  confirmLabel = "Okay",
  cancelLabel = "Cancel",
  width,
}: Omit<ConfirmInfo, "resolve">) =>
  new Promise<boolean>((resolve) => {
    confirmEvent.emit({title, content, comp, compProps, confirmLabel, cancelLabel, resolve, width});
  });

export default confirm;
